import React, { FC, ReactNode, useMemo } from "react";
import {
  IComponentCommonProps,
  IHeaderGroupComponentProps,
  TSDMCardScrollType,
  TSDMCardUpType,
  TSDMFeatureCardsList,
} from "@ldp/common-types";
import { FeatureCards } from "@ldp-web/component-library/ui/organisms/feature-cards";
import { useSimpleTranslation } from "@ldp/simple-i18n";
import { useRedirectLink } from "../../utils";
import { useSDMRouterContext } from "@ldp/sdm-contexts";
import { HeaderGroupComponent } from "../header-group";
import { WrapperComponent } from "../wrapper";
import { useAnalyticsContext, useFeatureFlags } from "@ldp/common-contexts";
import {
  SDMAnalyticEvents,
  TCTAContextAnalytics,
  THorizontalFeatureCardContainerCTAContext,
  TSDMComponentAnalyticsObject,
  TrackingFnContext,
  getCommonComponentContext,
} from "@ldp/sdm-analytics-utils";

/** Don't forget to add prop types */
export interface IHorizontalFeatureCardComponentProps
  extends IComponentCommonProps {
  children?: ReactNode;
  /** up number decides number of cards in a row */
  upNumber: TSDMCardUpType;
  /** component Id */
  componentId?: string;
  /** scroll type decides whether to show scroller or in grid */
  scrollOverflow: TSDMCardScrollType;
  /** header group details */
  headerGroup?: IHeaderGroupComponentProps;
  /** feature cards list */
  horizontalFeatureCards: TSDMFeatureCardsList[];
  /** content id of component */
  contentId?: string;
  /** analytics object */
  analytics?: TSDMComponentAnalyticsObject;
  /** id of component */
  id?: string;
}

/** Renders horizontal Feature Cards in a scroller or grid */
export const HorizontalFeatureCardCollection: FC<
  IHorizontalFeatureCardComponentProps
> = (props) => {
  const {
    contentId,
    componentId,
    upNumber,
    scrollOverflow,
    horizontalFeatureCards,
    headerGroup,
    styling,
    analytics,
    id,
  } = props;
  const i18n = useSimpleTranslation();
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const scrollOverflowValue =
    scrollOverflow.toLowerCase() as TSDMCardScrollType;

  const updatedCardsList = useMemo(() => {
    return horizontalFeatureCards.map((i) => {
      /** adds a new key called 'callToActionModal' to the object */
      if (i?.callToActionLink && "modalTitle" in i.callToActionLink) {
        const { callToActionLink, ...rest } = i;
        return {
          ...rest,
          callToActionModal: callToActionLink,
        };
      }
      /** adds a new key called 'launchChatbotAction' to the object
       * and remove callToActionButton.url if any is provided to prevent page redirect
       */
      if (i?.chatbotWidget?.dataHandle) {
        const launchChatbotWidget = () => {
          if (window.adaEmbed) {
            window.adaEmbed.toggle();
          } else return;
        };

        return {
          ...i,
          launchChatbotAction: launchChatbotWidget,
          callToActionButton: { text: i?.callToActionButton?.text },
        };
      } else {
        return {
          ...i,
        };
      }
    }) as TSDMFeatureCardsList[];
  }, [horizontalFeatureCards]);

  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow: snowplowEnabled } = getFeatureFlags();

  const trackFnWrapper: TrackingFnContext = (type, context) => {
    const { actionType, callToActionElement, position } =
      context as TCTAContextAnalytics;
    const dsModelData = analytics?.dsModel;
    const engineValue = dsModelData?.engine || "";
    const commonContext = getCommonComponentContext(
      snowplowEnabled,
      true,
      dsModelData
    );

    const headlineText = headerGroup?.headline?.text || "";
    const featureCardIndex = position ? position - 1 : 0;
    const selectedFeatureCard = updatedCardsList[featureCardIndex];
    const {
      contentId: horizontalFeatureCardContentId,
      headline: horizontalFeatureCardHeadline,
    } = selectedFeatureCard;

    const eventTrackingContext: THorizontalFeatureCardContainerCTAContext = {
      event: type as SDMAnalyticEvents,
      parentComponent: {
        id: contentId || "",
        type: componentId || "",
        name: headlineText,
        engine: engineValue,
      },
      component: {
        id: horizontalFeatureCardContentId || "",
        type: "horizontalFeatureCards",
        name: horizontalFeatureCardHeadline,
        click_element: callToActionElement?.text,
        click_action: "cta-click",
        click_url: callToActionElement?.url,
        engine: engineValue,
        position: position,
        number_of_children: horizontalFeatureCards.length,
        parent_id: contentId,
        hierarchy: [headlineText, horizontalFeatureCardHeadline],
      },
      customEngagement: {
        action: actionType,
      },
      ...commonContext,
    };

    trackEvent("heliosHorizontalFeatureCardContainer", eventTrackingContext);
  };

  return (
    <WrapperComponent
      data-testid="horizontal-feature-cards"
      styling={styling}
      id={id}
    >
      {headerGroup && (
        <HeaderGroupComponent
          headline={headerGroup.headline}
          paragraph={headerGroup.paragraph}
          callToAction={headerGroup.callToAction}
          desktopImage={headerGroup.desktopImage}
          noWrapper
        />
      )}
      <FeatureCards
        upNumber={upNumber}
        scrollType={scrollOverflowValue}
        cardsList={updatedCardsList as any}
        i18nskip={i18n("skip")}
        skipLinkLabel={i18n("a11ySection")}
        variant="horizontal"
        trackingFn={trackFnWrapper}
        cardClickAction={redirectToPage}
      />
    </WrapperComponent>
  );
};

export default HorizontalFeatureCardCollection;
